/* eslint-disable no-useless-escape */

import { format } from "date-fns";
import { MESSAGE_CONFIG } from "../config/messages-config";

export function numberWithCommas(number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function numberFormatter(name, data) {
    data = Math.round(data * 100) / 100;
    return `${name}:${data.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

export function currencyFormatter(name, data) {
    data = Math.round(data * 100) / 100;
    const number = formatnumber(data);
    return `${name ? `${name}: ` : ""}${
        !isNaN(number) || number !== "NaN"
            ? `$${number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
            : "--"
    }`;
}

export function percentFormatter(name, data) {
    data = Math.round(data * 100) / 100;
    return `${name}:${data.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`;
}

export function capitalize(s) {
    return (s && s[0].toUpperCase() + s.slice(1)) || "";
}

export function replaceLowercase(str) {
    return str.replaceAll(/[-_]+?/g, " ").toLowerCase();
}

export function removeFalsy(obj) {
    const newObj = {};
    Object.keys(obj).forEach((prop) => {
        if (prop === "types" && obj[prop].length > 0) {
            newObj[prop] = obj[prop];
        } else if (prop !== "types" && obj[prop]) {
            newObj[prop] = obj[prop];
        }
    });
    return newObj;
}

export function createColumnValueMap(object) {
    return Object.keys(object).map((key) => {
        return {
            column: key,
            value: object[key],
        };
    });
}

export function sortInSpecificOrder(data, sortOrder, isArray = true) {
    if (isArray) return data.sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b));
    return JSON.parse(JSON.stringify(data.length ? data : [data], sortOrder));
}

const unitlist = ["", "K", "M", "G"];
export const formatnumber = (number) => {
    const sign = Math.sign(number);
    let unit = 0;
    while (Math.abs(number) > 1000) {
        unit += 1;
        number = Math.floor(Math.abs(number) / 100) / 10;
    }
    return sign * number + unitlist[unit];
};

export const nFormatter = (num, digits = 2) => {
    const lookup = [
        { value: 1, symbol: "" },
        { value: 1e3, symbol: "k" },
        { value: 1e6, symbol: "M" },
        { value: 1e9, symbol: "G" },
        { value: 1e12, symbol: "T" },
        { value: 1e15, symbol: "P" },
        { value: 1e18, symbol: "E" },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    const item = lookup
        .slice()
        .reverse()
        .find((ele) => {
            return num >= ele.value;
        });
    return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
};

export function remove(array, key, value) {
    const index = array.findIndex((obj) => obj[key] === value);
    return index >= 0 ? [...array.slice(0, index), ...array.slice(index + 1)] : array;
}

export function getFilenameAndExtension(pathfilename) {
    const filenameextension = pathfilename.replace(/^.*[\\\/]/, "");
    const filename = filenameextension.substring(0, filenameextension.lastIndexOf("."));
    const ext = filenameextension.split(".").pop();

    return [filename, ext];
}

export function formatTime(time) {
    // Hours, minutes and seconds
    const hrs = time / 3600;
    const mins = (time % 3600) / 60;
    const secs = time % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let ret = "";
    if (hrs > 0) {
        ret += `${hrs}:${mins < 10 ? "0" : ""}`;
    }
    ret += `${mins}:${secs < 10 ? "0" : ""}`;
    ret += `${secs}`;
    return ret;
}

export const getBuildDate = (epoch) => {
    const buildDate = format(new Date(epoch), "dd-MM-yyyy HH:mm");
    return buildDate;
};

/**
 * Helper function to check if error indicates session expiry/unauthorized
 */
const isSessionExpiredError = (message, extensions) => {
    const code = extensions?.code || "";

    const sessionExpiredPatterns = [
        /token.*expired/i,
        /session.*expired/i,
        /jwt.*expired/i,
        /invalid.*token/i,
        /expired.*token/i,
        /authentication.*required/i,
        /not.*authenticated/i,
        /unauthenticated/i,
    ];

    const unauthorizedCodes = [
        "UNAUTHENTICATED",
        "TOKEN_EXPIRED",
        "JWT_EXPIRED",
        "INVALID_TOKEN",
        "SESSION_EXPIRED",
    ];

    if (unauthorizedCodes.includes(code)) {
        return true;
    }

    return sessionExpiredPatterns.some((pattern) => pattern.test(message));
};

/**
 * Helper function to check if error indicates forbidden/unauthorized access
 */
const isForbiddenError = (message, extensions) => {
    const code = extensions?.code || "";

    const forbiddenPatterns = [/forbidden/i, /access.*denied/i, /not.*authorized/i];

    const forbiddenCodes = ["FORBIDDEN", "UNAUTHORIZED", "ACCESS_DENIED"];

    if (forbiddenCodes.includes(code)) {
        return true;
    }

    return forbiddenPatterns.some((pattern) => pattern.test(message));
};

/**
 * Central error handler with MESSAGE_CONFIG integration
 * Maps GraphQL error codes to user-friendly messages
 * Also triggers session expired popup for unauthorized errors
 */
export const UnAuthenticationErrorHandler = (gqlErr, setDataError) => {
    const errorCode = gqlErr.message;
    const extensions = gqlErr.graphQLErrors?.[0]?.extensions || gqlErr.extensions;

    // Check for session expired errors and dispatch global event
    if (isSessionExpiredError(errorCode, extensions)) {
        console.log("🔒 UnAuthenticationErrorHandler: Session expired detected");
        window.dispatchEvent(new CustomEvent("sessionExpired", { detail: { type: "expired" } }));
        return; // Don't set error state, modal will handle it
    }

    // Check for forbidden errors and dispatch global event
    if (isForbiddenError(errorCode, extensions)) {
        console.log("🚫 UnAuthenticationErrorHandler: Forbidden access detected");
        window.dispatchEvent(new CustomEvent("sessionExpired", { detail: { type: "forbidden" } }));
        return; // Don't set error state, modal will handle it
    }

    // Use MESSAGE_CONFIG if available, otherwise use the raw message
    const errorMsg = MESSAGE_CONFIG[errorCode] || errorCode;
    setDataError({ error: true, errorMsg });
};

export const camalize = (text) => {
    text = text.replace(/[-_\s.]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""));
    return text.substr(0, 1).toLowerCase() + text.substr(1);
};

export const suppressCount = (count) => {
    return count > 99 ? `99+` : count;
};
